import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// True when the browser is not in an India timezone — these users see PayPal (USD)
// expanded by default; everyone else gets Cashfree/UPI first with PayPal as a link.
export function isLikelyInternational() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return !/^Asia\/(Kolkata|Calcutta)$/i.test(tz);
  } catch {
    return false;
  }
}

// Inside the Android WebView shell (UA is tagged "AstroFateApp/x.y.z") PayPal must be
// hidden — Google Play policy requires digital goods to be sold via Play Billing.
export function isNativeApp() {
  return /AstroFateApp\//i.test(navigator.userAgent || "");
}

// Small "or pay in …" separator, shared by the checkout surfaces.
export function PayDivider({ text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "14px 0 10px" }}>
      <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.12)" }} />
      <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem", letterSpacing: "0.06em", textTransform: "uppercase" }}>{text}</span>
      <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.12)" }} />
    </div>
  );
}

let sdkPromise = null;function loadPayPalSdk(clientId, currency) {
  if (window.paypal) return Promise.resolve(window.paypal);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}` +
      `&currency=${currency}&intent=capture&components=buttons&disable-funding=credit,card`;
    script.async = true;
    script.onload = () => resolve(window.paypal);
    script.onerror = () => { sdkPromise = null; reject(new Error("PayPal SDK failed to load")); };
    document.body.appendChild(script);
  });
  return sdkPromise;
}

/**
 * PayPal Smart Buttons for one-off USD purchases.
 * plan: "ai_chat" | "consultation" | "match"
 * onPaid(result) fires only after the backend has captured + verified the payment.
 */
export default function PayPalCheckout({ plan, payer = {}, onPaid, onError, label, variant = "secondary" }) {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | disabled | processing | error
  const [err, setErr] = useState("");
  const boxRef = useRef(null);
  const payerRef = useRef(payer);
  payerRef.current = payer;

  useEffect(() => {
    let alive = true;
    if (isNativeApp()) { setStatus("disabled"); return () => { alive = false; }; }
    axios.get(`${API}/payments/paypal/config`)
      .then(r => { if (alive) { r.data?.enabled ? setCfg(r.data) : setStatus("disabled"); } })
      .catch(() => { if (alive) setStatus("disabled"); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!cfg || !boxRef.current) return;
    let alive = true;
    loadPayPalSdk(cfg.client_id, cfg.currency).then(paypal => {
      if (!alive || !boxRef.current) return;
      boxRef.current.innerHTML = "";
      paypal.Buttons({
        style: { layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 44 },
        createOrder: async () => {
          setErr("");
          const p = payerRef.current || {};
          const res = await axios.post(`${API}/payments/paypal/create-order`, {
            plan, name: p.name || "", email: p.email || "", phone: p.phone || "",
          }, { withCredentials: true });
          return res.data.paypal_order_id;
        },
        onApprove: async (data) => {
          setStatus("processing");
          try {
            const res = await axios.post(
              `${API}/payments/paypal/capture/${data.orderID}`, {}, { withCredentials: true }
            );
            onPaid?.(res.data);
          } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "PayPal payment could not be confirmed. If you were charged, contact support.";
            setErr(msg);
            setStatus("ready");
            onError?.(msg);
          }
        },
        onCancel: () => { setErr("PayPal checkout was cancelled."); setStatus("ready"); },
        onError: (e) => {
          const msg = "PayPal could not process this payment. Please try again.";
          setErr(msg); setStatus("ready"); onError?.(msg);
          console.warn("PayPal error:", e);
        },
      }).render(boxRef.current).then(() => { if (alive) setStatus("ready"); });
    }).catch(() => {
      if (alive) { setStatus("error"); setErr("PayPal could not load. Please check your connection."); }
    });
    return () => { alive = false; };
  }, [cfg, plan]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === "disabled") return null;

  const price = cfg?.prices?.[plan];

  return (
    <div data-testid="paypal-checkout" style={{ marginTop: "14px" }}>
      {variant === "secondary" && <PayDivider text="or pay in USD" />}
      {price != null && (
        <p data-testid="paypal-price" style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", margin: "0 0 10px", textAlign: "center" }}>
          {label || "Paying from outside India?"} <strong style={{ color: "#FFD166" }}>${price.toFixed(2)} {cfg.currency}</strong> via PayPal
        </p>
      )}
      <div ref={boxRef} data-testid="paypal-buttons" style={{ minHeight: "48px", opacity: status === "processing" ? 0.5 : 1, pointerEvents: status === "processing" ? "none" : "auto" }} />
      {status === "loading" && (
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", textAlign: "center" }}>Loading PayPal…</p>
      )}
      {status === "processing" && (
        <p data-testid="paypal-processing" style={{ color: "#FFD166", fontSize: "0.8rem", textAlign: "center", marginTop: "8px" }}>
          Confirming your payment…
        </p>
      )}
      {err && (
        <p data-testid="paypal-error" style={{ color: "#fca5a5", fontSize: "0.8rem", textAlign: "center", marginTop: "8px" }}>{err}</p>
      )}
    </div>
  );
}
